import { Button, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { BoltIcon } from '@heroicons/react/24/solid';
import { clsx } from 'clsx';
import AUS from 'public:images/flags/AUS.svg';
import DEU from 'public:images/flags/DEU.svg';
import GBR from 'public:images/flags/GBR.svg';
import ITA from 'public:images/flags/ITA.svg';
import USA from 'public:images/flags/USA.svg';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import { Territory } from 'softcat:config/territory';
import { useTerritory } from 'softcat:hooks/use-territory.ts';

export const TerritorySelector = () => {
    const intl = useIntl();
    const { activeTerritory, territories, switchTerritory } = useTerritory();
    const navigate = useNavigate();
    const location = useLocation();

    const territoryTextColours: { [key in Territory]: string } = {
        [Territory.AUS]: 'text-blue-700',
        [Territory.DEU]: 'text-yellow-500',
        [Territory.GBR]: 'text-blue-600',
        [Territory.ITA]: 'text-green-600',
        [Territory.USA]: 'text-blue-600',
    };

    const territoryButtonColours: { [key in Territory]: string } = {
        [Territory.AUS]: 'ring-blue-700',
        [Territory.DEU]: 'ring-yellow-500',
        [Territory.GBR]: 'ring-blue-600',
        [Territory.ITA]: 'ring-green-600',
        [Territory.USA]: 'ring-blue-600',
    };

    const territoryFlags: { [key in Territory]: string } = {
        [Territory.AUS]: AUS,
        [Territory.DEU]: DEU,
        [Territory.GBR]: GBR,
        [Territory.ITA]: ITA,
        [Territory.USA]: USA,
    };

    return (
        <Menu as="div" className="relative inline-block text-left">
            <div>
                <MenuButton
                    className={clsx('inline-flex items-center gap-1 rounded-full bg-white py-2 pl-3 pr-2 shadow-sm ring-2 ring-inset hover:bg-gray-50', {
                        [territoryButtonColours[activeTerritory ?? Territory.GBR]]: activeTerritory && territories.length > 0,
                        [territoryTextColours[activeTerritory ?? Territory.GBR]]: activeTerritory && territories.length > 0,
                        'text-red-600 ring-red-600': territories.length === 0,
                    })}
                    data-testid="territory-selector"
                    disabled={territories.length === 0}
                >
                    <BoltIcon
                        className={clsx('size-4 stroke-1 drop-shadow-sm', {
                            'text-amber-400': territories.length > 0,
                            'text-red-600': territories.length === 0,
                        })}
                    />
                    <div className={'pr-2 text-sm font-semibold'}>
                        {territories.length > 0 && activeTerritory ? (
                            <img alt={activeTerritory} className="inline-block size-6" src={territoryFlags[activeTerritory]} />
                        ) : (
                            intl.formatMessage({ id: 'TERRITORY_SELECTOR_ERROR' })
                        )}
                    </div>
                </MenuButton>
            </div>

            <MenuItems
                anchor={'bottom end'}
                className="absolute z-50 mt-2 w-auto origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                transition
            >
                <div className={'w-40 bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-500'}>
                    <FormattedMessage id={'TERRITORY_SELECTOR_DESCRIPTION'} />
                </div>
                {territories.sort().map((territory) => {
                    return (
                        <MenuItem key={territory}>
                            <Button
                                className="relative flex w-full items-center gap-x-2 py-2 pl-4 pr-8 text-left text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                                data-testid={`select-territory-${territory}`}
                                onClick={() => {
                                    switchTerritory(territory);

                                    navigate(location.pathname);
                                }}
                            >
                                <img alt={territory} className="inline-block size-6" src={territoryFlags[territory]} />
                                <div>{territory}</div>
                                {territory === activeTerritory && (
                                    <div className={'absolute right-0 px-3 py-2'}>
                                        <CheckIcon className={'size-4 text-indigo-700'} data-testid={`selected-territory-${territory}`} />
                                    </div>
                                )}
                            </Button>
                        </MenuItem>
                    );
                })}
            </MenuItems>
        </Menu>
    );
};
